import React from 'react';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import {detect} from 'detect-browser';
import {PromotionListItem} from '@practicegenius/domain/src/data/promotions/promotion';
import {IMT} from '@practicegenius/pg-util/src/StringUtils';
import {PromotionCard} from './PromotionCard';
import {ActionButton} from './sharedUI';
import lang from '../lang';

type Props = {
  title: string;
  promotions: PromotionListItem[];
};

const VISIBLE_PROMOTIONS_THRESHOLD = 3;

const browser = detect();
const isIEOrLegacyEdge = browser?.name === 'ie' || browser?.name === 'edge';

export const PromotionGridSection: React.FC<Props> = props => {
  const [showMore, setShowMore] = React.useState(false);
  const [scrollTop, setScrollTop] = React.useState(0);

  /**
   * Please refrain from touching. Beatings will commence otherwise.
   * This is here to make it so the page expands down instead of up when the button is clicked.
   * If it breaks, it might be because somebody removed some overflow styling from content-wrapper.
   */
  const contentRef = React.useRef<HTMLDivElement>(
    document.querySelector('.content-wrapper'),
  );
  React.useLayoutEffect(() => {
    /* Support IE11 */
    !isIEOrLegacyEdge && contentRef.current?.scrollTo({top: scrollTop});
  });

  const visiblePromotions = showMore
    ? props.promotions
    : R.take(VISIBLE_PROMOTIONS_THRESHOLD, props.promotions);

  return (
    <>
      <Box mb={3}>
        <Typography
          variant="h5"
          sx={theme => ({color: theme.palette.text.primary})}
        >
          {props.title}
        </Typography>
      </Box>
      <Grid container spacing={3}>
        {visiblePromotions.map(promotion => (
          <Grid key={promotion.title} item xs={12} md={4}>
            <PromotionCard promotion={promotion} />
          </Grid>
        ))}
      </Grid>
      {props.promotions.length > VISIBLE_PROMOTIONS_THRESHOLD && (
        <Box mt={3}>
          <ActionButton
            onClick={() => {
              setShowMore(!showMore);
              contentRef.current && setScrollTop(contentRef.current.scrollTop);
            }}
            sx={{
              color: 'var(--template-color-1)',
              '&:hover': {color: 'var(--template-color-1)'},
            }}
          >
            {IMT(lang.main.showMore, {
              quantity: showMore ? 'Fewer' : 'More',
              title: props.title,
            })}
          </ActionButton>
        </Box>
      )}
    </>
  );
};
